import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { useInView } from '../../../hooks/useInView';

const EngineSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  const diagramY = useTransform(scrollYProgress, [0, 1], ['4%', '-4%']);

  return (
    <section ref={sectionRef} className="relative bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#080808] py-16 md:py-24 lg:py-40 overflow-hidden">
      <motion.div style={{ y: bgY }} className="hidden lg:block absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-accent-blue/8 blur-[200px]" />
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-red-500/5 blur-[150px]" />
      </motion.div>

      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-accent-blue/8 blur-[120px]" />
      </div>

      <div ref={ref} className="max-w-[1200px] mx-auto px-6 md:px-8 relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-[32px] md:text-5xl lg:text-6xl font-bold text-white text-center mb-10 md:mb-16 lg:mb-20"
        >
          How Bottlenecks Work
        </motion.h2>

        {/* DESKTOP diagram */}
        <motion.div
          style={{ y: diagramY }}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block relative"
        >
          <svg viewBox="0 0 1000 920" className="w-full h-auto">
            <defs>
              <linearGradient id="cardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1a1a2e" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#0f0f1a" stopOpacity="0.95" />
              </linearGradient>
              <linearGradient id="bottleneckGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FF3B30" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#FF3B30" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="interventionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#0066FF" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#00CFFF" stopOpacity="0.2" />
              </linearGradient>
              <filter id="cardGlow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="strongGlow">
                <feGaussianBlur stdDeviation="8" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* ── INPUT CARDS ── */}
            {[
              { x: 80,  label: 'Team',    icon: '👥' },
              { x: 303, label: 'Product', icon: '🚀' },
              { x: 526, label: 'Capital', icon: '💰' },
              { x: 749, label: 'Market',  icon: '🎯' },
            ].map(({ x, label, icon }, i) => (
              <motion.g
                key={label}
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                <rect x={x} y="48" width="171" height="96" rx="12" fill="#000000" opacity="0.3" filter="url(#cardGlow)" />
                <rect x={x} y="45" width="171" height="96" rx="12" fill="url(#cardGradient)" stroke="#0066FF" strokeWidth="1.2" opacity="0.85" />
                <rect x={x} y="45" width="171" height="96" rx="12" fill="none" stroke="#0066FF" strokeWidth="1.5" opacity="0.25" filter="url(#cardGlow)" />
                <text x={x + 85} y="86" textAnchor="middle" fontSize="28" opacity="0.9">{icon}</text>
                <text x={x + 85} y="120" textAnchor="middle" fill="#ffffff" fontSize="15" fontWeight="600" letterSpacing="0.5">{label}</text>
              </motion.g>
            ))}

            {/* ── CONNECTOR LINES: cards → bottleneck ── */}
            {[
              { x1: 165, y1: 141, x2: 500, y2: 308 },
              { x1: 388, y1: 141, x2: 500, y2: 308 },
              { x1: 611, y1: 141, x2: 500, y2: 308 },
              { x1: 834, y1: 141, x2: 500, y2: 308 },
            ].map(({ x1, y1, x2, y2 }, i) => (
              <motion.line
                key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#0066FF"
                strokeOpacity="0.45"
                strokeWidth="1.8"
                strokeDasharray="6,4"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.6 + i * 0.1 }}
              >
                <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="2s" repeatCount="indefinite" />
              </motion.line>
            ))}

            {/* ── BOTTLENECK FUNNEL ── */}
            <motion.g
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.7, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.circle
                cx="500" cy="358" r="95"
                fill="#FF3B30" opacity="0.12"
                filter="url(#strongGlow)"
                animate={{ opacity: [0.12, 0.22, 0.12] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <path d="M 400 308 L 600 308 L 574 398 L 426 398 Z" fill="url(#bottleneckGradient)" stroke="#FF3B30" strokeWidth="2" />
              <line x1="428" y1="330" x2="572" y2="330" stroke="#FF3B30" strokeWidth="1" opacity="0.35" />
              <line x1="434" y1="352" x2="566" y2="352" stroke="#FF3B30" strokeWidth="1" opacity="0.35" />
              <line x1="440" y1="374" x2="560" y2="374" stroke="#FF3B30" strokeWidth="1" opacity="0.35" />
              <rect x="440" y="300" width="120" height="20" rx="10" fill="#FF3B30" />
              <text x="500" y="314" textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="700" letterSpacing="1.5">CONSTRAINT</text>
              <text x="500" y="348" textAnchor="middle" fill="#FF5A50" fontSize="17" fontWeight="800" letterSpacing="1">GROWTH</text>
              <text x="500" y="372" textAnchor="middle" fill="#FF5A50" fontSize="17" fontWeight="800" letterSpacing="1">BOTTLENECK</text>
            </motion.g>

            {/* ── CONNECTOR: funnel → intervention ── */}
            <motion.line
              x1="500" y1="398" x2="500" y2="468"
              stroke="#404040" strokeWidth="2" strokeDasharray="5,4"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 1.1 }}
            />

            {/* ── INTERVENTION CIRCLE ── */}
            <motion.g
              initial={{ opacity: 0, scale: 0.7 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.7, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.circle
                cx="500" cy="538" r="78"
                fill="#0066FF" opacity="0.18"
                filter="url(#strongGlow)"
                animate={{ opacity: [0.18, 0.32, 0.18] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              />
              <circle cx="500" cy="538" r="66" fill="url(#interventionGradient)" stroke="#0066FF" strokeWidth="2.5" />
              <circle cx="500" cy="538" r="66" fill="none" stroke="#00CFFF" strokeWidth="1" opacity="0.45" />
              <motion.path
                d="M 514 508 L 489 538 L 504 538 L 484 568"
                fill="none" stroke="#00CFFF" strokeWidth="5"
                strokeLinecap="round" strokeLinejoin="round"
                animate={{ opacity: [1, 0.55, 1] }}
                transition={{ duration: 1.8, repeat: Infinity }}
              />
              <text x="500" y="626" textAnchor="middle" fill="#4da6ff" fontSize="14" fontWeight="700" letterSpacing="2">BOTTLENECK</text>
            </motion.g>

            {/* ── ACCELERATED FLOW LINE ── */}
            <motion.line
              x1="500" y1="604" x2="500" y2="678"
              stroke="#0066FF" strokeWidth="3" strokeOpacity="0.9"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 1.55 }}
            />

            {/* ── EXPANSION RAYS ── */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 1.7 }}
            >
              {[340, 415, 500, 585, 660].map((x, i) => (
                <motion.g key={i}>
                  <motion.line
                    x1="500" y1="678" x2={x} y2="778"
                    stroke="#00CFFF" strokeWidth="2.5" strokeLinecap="round" strokeOpacity="0.85"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 0.85 } : {}}
                    transition={{ duration: 0.5, delay: 1.75 + i * 0.06 }}
                  />
                  <motion.circle
                    cx={x} cy="778" r="4.5" fill="#00CFFF"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.4, delay: 1.95 + i * 0.06 }}
                  />
                </motion.g>
              ))}
            </motion.g>

            {/* ── ARROW: BOTTLENECK REMOVAL → GROWTH UNLOCKED ──
                Plain static SVG — no framer-motion pathLength, guaranteed visible.
                Curve starts at right edge of "REMOVAL" text (~x=578, y=644),
                arcs out to the right, then lands at x=645 just above "GROWTH UNLOCKED" (y=820).
            ── */}
            <g>
              {/* Curved arrow path */}
              <path
                d="M 578 644 Q 800 745 648 816"
                fill="none"
                stroke="#00CFFF"
                strokeWidth="2.5"
                strokeOpacity="0.9"
                strokeLinecap="round"
              />
              {/* Arrowhead triangle at endpoint (648, 816)
                  End tangent direction: 2*(648-800, 816-745) = (-304, 142)
                  Unit ≈ (-0.906, 0.423)  Perp ≈ (0.423, 0.906)
                  tip=(648,816) back=(648+13*0.906, 816-13*0.423)=(659.8,810.5)
                  left=(659.8+6*0.423, 810.5+6*0.906)=(662.3,815.9)
                  right=(659.8-6*0.423, 810.5-6*0.906)=(657.3,805.1) */}
              <polygon
                points="648,816 662,816 657,805"
                fill="#00CFFF"
                opacity="0.9"
              />
              {/* "leads to" label riding the curve */}
              <text
                x="748"
                y="700"
                textAnchor="middle"
                fill="#00CFFF"
                fontSize="11"
                fontWeight="600"
                letterSpacing="1"
                opacity="0.8"
                transform="rotate(26, 748, 700)"
              >
                leads to
              </text>
            </g>

            {/* ── GROWTH UNLOCKED ── */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.7, delay: 2.1 }}
            >
              <text x="500" y="835" textAnchor="middle" fill="#ffffff" fontSize="21" fontWeight="700" letterSpacing="2">
                GROWTH UNLOCKED
              </text>
              <motion.ellipse
                cx="500" cy="848" rx="175" ry="14"
                fill="#0066FF" opacity="0.18"
                animate={{ opacity: [0.18, 0.28, 0.18] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </motion.g>
          </svg>
        </motion.div>

        {/* MOBILE diagram */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:hidden relative px-4"
        >
          <svg viewBox="0 0 400 800" className="w-full h-auto max-w-[360px] mx-auto">
            <defs>
              <linearGradient id="mobileCardGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1a1a2e" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#0f0f1a" stopOpacity="0.95" />
              </linearGradient>
              <filter id="mobileGlow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <g>
              <rect x="60" y="20" width="280" height="80" rx="10" fill="url(#mobileCardGrad)" stroke="#0066FF" strokeWidth="1" opacity="0.8" />
              <text x="200" y="56" textAnchor="middle" fill="#ffffff" fontSize="13" fontWeight="600">Team  ·  Product</text>
              <text x="200" y="80" textAnchor="middle" fill="#ffffff" fontSize="13" fontWeight="600">Capital  ·  Market</text>
            </g>

            <line x1="200" y1="100" x2="200" y2="180" stroke="#0066FF" strokeOpacity="0.4" strokeWidth="2" strokeDasharray="4,3" />

            <g>
              <circle cx="200" cy="240" r="60" fill="#FF3B30" opacity="0.12" filter="url(#mobileGlow)" />
              <path d="M 155 210 L 245 210 L 232 278 L 168 278 Z" fill="#FF3B30" fillOpacity="0.18" stroke="#FF3B30" strokeWidth="2" />
              <rect x="165" y="202" width="70" height="16" rx="8" fill="#FF3B30" />
              <text x="200" y="214" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700" letterSpacing="1">CONSTRAINT</text>
              <text x="200" y="244" textAnchor="middle" fill="#FF5A50" fontSize="13" fontWeight="800">GROWTH</text>
              <text x="200" y="262" textAnchor="middle" fill="#FF5A50" fontSize="13" fontWeight="800">BOTTLENECK</text>
            </g>

            <line x1="200" y1="278" x2="200" y2="340" stroke="#404040" strokeWidth="2" strokeDasharray="3,3" />

            <g>
              <circle cx="200" cy="392" r="56" fill="#0066FF" opacity="0.18" filter="url(#mobileGlow)" />
              <circle cx="200" cy="392" r="50" fill="#0066FF" fillOpacity="0.14" stroke="#0066FF" strokeWidth="2" />
              <path d="M 210 372 L 190 392 L 200 392 L 185 412" fill="none" stroke="#00CFFF" strokeWidth="4" strokeLinecap="round" />
              <text x="200" y="462" textAnchor="middle" fill="#4da6ff" fontSize="12" fontWeight="700" letterSpacing="1">BOTTLENECK</text>
              <text x="200" y="478" textAnchor="middle" fill="#4da6ff" fontSize="12" fontWeight="700" letterSpacing="1">REMOVAL</text>
            </g>

            <line x1="200" y1="444" x2="200" y2="510" stroke="#0066FF" strokeWidth="2.5" strokeOpacity="0.9" />

            <g>
              {[155, 175, 200, 225, 245].map((x, i) => (
                <g key={i}>
                  <line x1="200" y1="510" x2={x} y2="590" stroke="#00CFFF" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
                  <circle cx={x} cy="590" r="3.5" fill="#00CFFF" />
                </g>
              ))}
              {/* Mobile arrow: REMOVAL → GROWTH UNLOCKED */}
              <path d="M 252 478 Q 330 560 262 614" fill="none" stroke="#00CFFF" strokeWidth="2" strokeOpacity="0.85" strokeLinecap="round" />
              <polygon points="262,618 272,607 263,602" fill="#00CFFF" opacity="0.85" />
              <text x="200" y="636" textAnchor="middle" fill="#ffffff" fontSize="15" fontWeight="700" letterSpacing="1.5">GROWTH UNLOCKED</text>
            </g>
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default EngineSection;
